/** Native leave waiting is not a mutation. Reads the active form, and fences late confirmations. */
import { useLayoutEffect, useMemo } from 'react'
import { STUDY_BUILDER_SAVE_BAR_ID } from './useStudyBuilderSaveBar'
import { useBuilderEvaluationInput } from './useBuilderEvaluationWork'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { NavigationClient } from '../../shopifyNavigation'
import { createShopifyLeaveGuard, registerShopifyLeaveGuard } from '../../shopifyNavigation'
import { reportClientDiagnostic } from '../../clientDiagnostics'
import { shopifyComponentCopy } from '@testfeed/product-language'

export function reportShopifyNavigationFailure(shopify: ShopifyAppBridge & NavigationClient, _cause: unknown) {
  try {
    shopify.toast?.show(shopifyComponentCopy.studyBuilderPage.couldNotLeave, { isError: true })
  } catch (toastFailure) {
    console.error('[shopify-navigation] Native toast unavailable', toastFailure)
  }
  void reportClientDiagnostic(shopify, {
    kind: 'error',
    schema: 'shopify-navigation',
    paths: ['leave_confirmation_failed'],
    code: 'SHOPIFY_LEAVE_CONFIRMATION_FAILED',
  })
}
export function useShopifyAppNavigation(input: {
  visit: string
  shopify: ShopifyAppBridge & NavigationClient
  readDirty: () => boolean
}) {
  const latest = useBuilderEvaluationInput(input)
  const guard = useMemo(
    () =>
      createShopifyLeaveGuard({
        readDirty: () => latest.current.readDirty(),
        hideSaveBar: () => latest.current.shopify.saveBar.hide(STUDY_BUILDER_SAVE_BAR_ID),
        confirm: () => latest.current.shopify.saveBar.leaveConfirmation(),
        onFailure: (cause) => reportShopifyNavigationFailure(latest.current.shopify, cause),
      }),
    [input.visit],
  )
  // Layout cleanup fences old visits before any queued native completion can commit.
  useLayoutEffect(() => registerShopifyLeaveGuard(input.shopify, guard), [input.shopify, guard])
  return guard
}
